import { useEffect, useSyncExternalStore } from "react";
import { action, subscribe } from "./api.ts";

/**
 * Visitors waiting at reception for the signed-in person, for the bar under the top bar. One
 * request for the whole app, refreshed when a visitor signs in or out and every five minutes.
 */

export interface WaitingVisitor {
  id: string;
  name: string;
  company: string | null;
  location_name: string;
  drink_name: string | null;
}

let waiting: WaitingVisitor[] = [];
const listeners = new Set<() => void>();
let users = 0;
let stop: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function publish(next: WaitingVisitor[]) {
  if (JSON.stringify(next) === JSON.stringify(waiting)) return;
  waiting = next;
  for (const l of listeners) l();
}

async function refresh() {
  try {
    const mine = await action<{ waiting: WaitingVisitor[] }>(
      "frontdesk.mine",
      {},
    );
    publish(
      mine.waiting.map(({ id, name, company, location_name, drink_name }) => ({
        id,
        name,
        company,
        location_name,
        drink_name,
      })),
    );
  } catch {
    // Offline or signed out: no bar.
  }
}

export function useWaitingVisitors(enabled: boolean): WaitingVisitor[] {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => waiting,
  );
  useEffect(() => {
    if (!enabled) return;
    users++;
    if (!stop) {
      void refresh();
      stop = subscribe(["frontdesk"], () => void refresh());
      timer = setInterval(() => void refresh(), 300_000);
    }
    return () => {
      users--;
      if (users === 0) {
        stop?.();
        stop = null;
        if (timer) clearInterval(timer);
        timer = null;
      }
    };
  }, [enabled]);
  return enabled ? value : [];
}

/** "On my way": reception sees it at once. */
export async function comingFor(id: string) {
  await action("frontdesk.coming", { id });
  await refresh();
}
export const refreshVisitors = () => void refresh();
